import { useState, useMemo } from 'react'
import type { JSX } from 'react'
import type { CSSProperties } from 'react'

import { clsx } from 'clsx'

import { NewspaperIcon } from '@buffer-mono/popcorn'
import { getUrl } from '@buffer-mono/shared-utils/bibe'

import styles from './FeedIcon.module.css'

const sizeMap = {
  xsmall: 12,
  small: 16,
  medium: 32,
  large: 48,
}

type FeedItemIconProps = {
  feedName: string
  url?: string
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  className?: string
  thumbnailUrl?: string
}

export const FeedItemIcon = (props: FeedItemIconProps): JSX.Element => {
  const {
    feedName,
    url,
    size = 'small',
    className,
    thumbnailUrl,
    ...rest
  } = props
  const [hasError, setHasError] = useState(false)

  const domain = useMemo(() => {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }, [url])

  const iconSize = sizeMap[size] || 16
  /**
   * Some icon sizes make the avatar style look a little weird
   * As these icons are dynamic it's hard to get the size right
   * So we're overriding the size only for the Buffer icon to make it look better
   */
  const bufferFaviconUrl = getUrl(
    'https://images.buffer.com/proxy/100x100/https://buffer.com/static/icons/favicon.svg',
  )
  const bufferIconOverride = thumbnailUrl === bufferFaviconUrl ? 30 : undefined
  const feedIconStyle: CSSProperties & Record<`--${string}`, string> = {
    '--feed-icon-size': `${iconSize}px`,
    '--feed-icon-border-size': `${
      size === 'xsmall' || size === 'small' ? 1 : 2
    }px`,
  }
  const imageStyle: CSSProperties & Record<`--${string}`, string> = {
    '--feed-icon-size': `${bufferIconOverride ?? iconSize}px`,
  }
  const fallbackStyle: CSSProperties & Record<`--${string}`, string> = {
    '--feed-icon-size': `${iconSize / 1.5}px`,
  }

  return (
    <div className={styles.feedIcon} style={feedIconStyle}>
      {(thumbnailUrl || domain) && !hasError ? (
        <img
          aria-hidden="true"
          src={thumbnailUrl || `https://icons.duckduckgo.com/ip3/${domain}.ico`}
          alt={`${feedName} icon`}
          onError={(): void => {
            setHasError(true)
          }}
          className={clsx(styles.image, className)}
          style={imageStyle}
          {...rest}
        />
      ) : (
        <NewspaperIcon className={className} style={fallbackStyle} {...rest} />
      )}
    </div>
  )
}
